/**
 * User account panel — projects, DIGIPIN status, fair-use meters.
 * Route: /account (protected)
 */
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Container,
  LinearProgress,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { PublicSiteChrome, publicPageSx } from './site/PublicSiteChrome';
import Footer from './Footer';
import { useAppSelector } from '../store/hooks';
import { listCloudProjects, type ProjectListItem } from '../services/cloudProjectService';
import { fetchMyDigipinAccess } from '../services/digipinAccessService';
import { apiFetch, buildAuthHeaders } from '../services/apiClient';
import { fetchStudioStatus } from '../services/paymentsService';

const TOKENS = {
  accent: '#D97706',
  secondary: '#3B82F6',
  green: '#10B981',
  bgCard: 'rgba(15, 23, 42, 0.75)',
  border: 'rgba(219, 234, 254, 0.15)',
  textPrimary: '#F8FAFC',
  textSecondary: '#94A3B8',
};

const FALLBACK_LIMITS = {
  maxProjects: 25,
  maxLayers: 50,
};

export default function UserAccountPage() {
  const user = useAppSelector((s) => s.auth.user);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [layerCount, setLayerCount] = useState(0);
  const [projectLimit, setProjectLimit] = useState(FALLBACK_LIMITS.maxProjects);
  const [layerLimit, setLayerLimit] = useState(FALLBACK_LIMITS.maxLayers);
  const [planTier, setPlanTier] = useState<'community' | 'studio'>('community');
  const [studioActive, setStudioActive] = useState(false);
  const [studioUntil, setStudioUntil] = useState<string | null>(null);
  const [recentPayments, setRecentPayments] = useState<
    Array<{
      plan_code: string;
      amount_paise: number;
      status: string;
      paid_at?: string;
      razorpay_payment_id?: string;
    }>
  >([]);
  const [digipinKeys, setDigipinKeys] = useState(0);
  const [digipinStatus, setDigipinStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [projList, digipin, layersRes, fairRes, studioRes] = await Promise.all([
          listCloudProjects(),
          fetchMyDigipinAccess().catch(() => null),
          apiFetch('/api/layers', {
            method: 'GET',
            credentials: 'include',
            headers: buildAuthHeaders(),
            timeoutMs: 8000,
          }).catch(() => null),
          apiFetch('/api/projects/fair-use', {
            method: 'GET',
            credentials: 'include',
            headers: buildAuthHeaders(),
            timeoutMs: 8000,
          }).catch(() => null),
          fetchStudioStatus().catch(() => null),
        ]);
        if (cancelled) return;
        setProjects(projList.filter((p) => !String(p.name).startsWith('[Local]')));
        if (digipin && (digipin as { success?: boolean }).success) {
          const d = digipin as {
            keys?: unknown[];
            request?: { status?: string } | null;
          };
          setDigipinKeys(Array.isArray(d.keys) ? d.keys.length : 0);
          setDigipinStatus(d.request?.status || (d.keys?.length ? 'active' : null));
        }
        if (layersRes && layersRes.ok) {
          const json = await layersRes.json().catch(() => ({}));
          if (json.success && Array.isArray(json.data)) {
            setLayerCount(json.data.length);
          }
        }
        if (fairRes && fairRes.ok) {
          const fj = await fairRes.json().catch(() => ({}));
          if (fj.success && fj.usage) {
            setProjectLimit(fj.usage.projects?.limit ?? FALLBACK_LIMITS.maxProjects);
            setLayerLimit(fj.usage.layers?.limit ?? FALLBACK_LIMITS.maxLayers);
            if (fj.usage.tier === 'studio' || fj.usage.studioActive) {
              setPlanTier('studio');
              setStudioActive(true);
              setStudioUntil(fj.usage.studioActiveUntil || null);
            }
            if (typeof fj.usage.layers?.used === 'number') {
              setLayerCount(fj.usage.layers.used);
            }
          }
        }
        if (studioRes?.success) {
          setStudioActive(!!studioRes.entitlement?.active);
          setStudioUntil(studioRes.entitlement?.activeUntil || null);
          if (studioRes.entitlement?.active) setPlanTier('studio');
          setRecentPayments(
            (studioRes.payments || [])
              .filter((p) => p.status === 'paid')
              .slice(0, 5)
              .map((p) => ({
                plan_code: p.plan_code,
                amount_paise: p.amount_paise,
                status: p.status,
                paid_at: p.paid_at,
                razorpay_payment_id: p.razorpay_payment_id,
              }))
          );
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load account');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const projectCloudCount = projects.length;
  const projectPct = Math.min(100, Math.round((projectCloudCount / projectLimit) * 100));
  const layerPct = Math.min(100, Math.round((layerCount / layerLimit) * 100));

  return (
    <Box sx={{ ...publicPageSx, bgcolor: '#0B132B', color: TOKENS.textPrimary }}>
      <PublicSiteChrome title="GeoVera" subtitle="Account" active="account" />
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Typography
          sx={{ fontSize: { xs: 28, md: 34 }, fontWeight: 800, letterSpacing: '-0.03em', mb: 0.5 }}
        >
          Your account
        </Typography>
        <Typography sx={{ color: TOKENS.textSecondary, mb: 2, fontSize: 14 }}>
          {user?.email || 'Signed in'} · fair-use meters below
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, alignItems: 'center' }}>
          <Chip
            size="small"
            label={studioActive ? 'GeoVera Studio (paid)' : 'Community (free)'}
            sx={{
              fontWeight: 800,
              bgcolor: studioActive ? 'rgba(217,119,6,0.2)' : 'rgba(16,185,129,0.15)',
              color: studioActive ? '#FBBF24' : TOKENS.green,
            }}
          />
          {studioActive && studioUntil && (
            <Typography sx={{ fontSize: 12, color: TOKENS.textSecondary }}>
              Active until {new Date(studioUntil).toLocaleDateString('en-IN')} · SIFA Geolabs /
              Razorpay
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
          <Button
            component={RouterLink}
            to="/map"
            variant="contained"
            startIcon={<MapOutlinedIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: TOKENS.green,
              color: '#042f1a',
              '&:hover': { bgcolor: '#34D399' },
            }}
          >
            Open free map
          </Button>
          <Button
            component={RouterLink}
            to="/dashboard"
            variant="outlined"
            endIcon={<ArrowOutwardIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: TOKENS.border,
              color: TOKENS.textPrimary,
            }}
          >
            Full WebGIS workspace
          </Button>
          <Button
            component={RouterLink}
            to="/developers/digipin"
            variant="outlined"
            startIcon={<KeyOutlinedIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: TOKENS.border,
              color: TOKENS.textPrimary,
            }}
          >
            DIGIPIN API
          </Button>
          {!studioActive && (
            <Button
              component={RouterLink}
              to="/pricing#studio-checkout"
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: TOKENS.accent,
                '&:hover': { bgcolor: '#B45309' },
              }}
            >
              Upgrade to Studio
            </Button>
          )}
          <Button
            component={RouterLink}
            to="/pricing"
            variant="text"
            sx={{ textTransform: 'none', color: TOKENS.secondary, fontWeight: 600 }}
          >
            Pricing
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: TOKENS.secondary }} />
          </Box>
        )}
        {error && (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              bgcolor: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.3)',
            }}
          >
            <Typography sx={{ color: '#FCA5A5', fontSize: 13 }}>{error}</Typography>
          </Paper>
        )}

        {!loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Fair-use meters */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: TOKENS.bgCard,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 3,
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 2 }}>
                Fair-use ({planTier === 'studio' ? 'Studio' : 'Community'})
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontSize: 13, color: TOKENS.textSecondary }}>
                    Cloud projects
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                    {projectCloudCount} / {projectLimit}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={projectPct}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    bgcolor: 'rgba(255,255,255,0.08)',
                    '& .MuiLinearProgress-bar': { bgcolor: TOKENS.secondary },
                  }}
                />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontSize: 13, color: TOKENS.textSecondary }}>
                    Vector layers
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                    {layerCount} / {layerLimit}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={layerPct}
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    bgcolor: 'rgba(255,255,255,0.08)',
                    '& .MuiLinearProgress-bar': { bgcolor: TOKENS.accent },
                  }}
                />
              </Box>
              <Typography sx={{ mt: 1.5, fontSize: 11, color: '#64748B' }}>
                {planTier === 'studio'
                  ? 'Studio paid tier: higher project and layer caps. Map tools stay fully available.'
                  : 'Community is free forever. Soft caps protect hosting — upgrade to Studio for higher volume.'}
              </Typography>
            </Paper>

            {recentPayments.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  bgcolor: TOKENS.bgCard,
                  border: `1px solid ${TOKENS.border}`,
                  borderRadius: 3,
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 1.5 }}>
                  Payments (SIFA Geolabs)
                </Typography>
                {recentPayments.map((p, i) => (
                  <Box
                    key={`${p.razorpay_payment_id || p.plan_code}-${i}`}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      py: 1,
                      borderBottom: `1px solid ${TOKENS.border}`,
                    }}
                  >
                    <Typography sx={{ fontSize: 13 }}>
                      {p.plan_code} · ₹{(p.amount_paise / 100).toLocaleString('en-IN')}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: TOKENS.textSecondary }}>
                      {p.paid_at ? new Date(p.paid_at).toLocaleDateString('en-IN') : p.status}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            )}

            {/* Projects */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: TOKENS.bgCard,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CloudOutlinedIcon sx={{ color: TOKENS.secondary, fontSize: 22 }} />
                <Typography sx={{ fontWeight: 800, fontSize: 16 }}>Cloud projects</Typography>
              </Box>
              {projects.length === 0 ? (
                <Typography sx={{ color: TOKENS.textSecondary, fontSize: 13 }}>
                  No cloud projects yet. Open the map, draw or import, then save a project from the
                  workspace.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {projects.slice(0, 12).map((p) => (
                    <Box
                      key={p.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                        borderBottom: `1px solid ${TOKENS.border}`,
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{p.name}</Typography>
                        <Typography sx={{ fontSize: 11, color: TOKENS.textSecondary }}>
                          Updated {p.updatedAt ? new Date(p.updatedAt).toLocaleString() : '—'}
                        </Typography>
                      </Box>
                      <Button
                        component={RouterLink}
                        to="/dashboard"
                        size="small"
                        sx={{ textTransform: 'none', color: TOKENS.secondary }}
                      >
                        Open workspace
                      </Button>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>

            {/* DIGIPIN */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: TOKENS.bgCard,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <KeyOutlinedIcon sx={{ color: TOKENS.accent, fontSize: 22 }} />
                <Typography sx={{ fontWeight: 800, fontSize: 16 }}>DIGIPIN API</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <Chip
                  size="small"
                  label={digipinStatus ? `Request: ${digipinStatus}` : 'No request yet'}
                  sx={{ bgcolor: 'rgba(217,119,6,0.15)', color: '#FBBF24', fontWeight: 600 }}
                />
                <Chip
                  size="small"
                  label={`${digipinKeys} key(s)`}
                  sx={{ bgcolor: 'rgba(59,130,246,0.15)', color: '#93C5FD', fontWeight: 600 }}
                />
              </Box>
              <Button
                component={RouterLink}
                to="/developers/digipin"
                variant="outlined"
                size="small"
                sx={{
                  textTransform: 'none',
                  borderColor: TOKENS.border,
                  color: TOKENS.textPrimary,
                  fontWeight: 600,
                }}
              >
                Manage DIGIPIN access
              </Button>
            </Paper>

            {/* Shares note */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: TOKENS.bgCard,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 3,
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 1 }}>Public shares</Typography>
              <Typography sx={{ color: TOKENS.textSecondary, fontSize: 13, lineHeight: 1.5 }}>
                Shared maps use links like <code>/m/&lt;layer-id&gt;</code>. Create a share from the
                map workspace or property card. You have {layerCount} layer(s) that can be shared
                when published.
              </Typography>
            </Paper>
          </Box>
        )}
      </Container>
      <Footer compact />
    </Box>
  );
}
